import type { SessionInfo } from "@opencode/client/promise"
import { Button } from "@opencode/ui/button"
import { useDialog } from "@opencode/ui/context/dialog"
import { Dialog, DialogFooter, DialogHeader, DialogTitleGroup } from "@opencode/ui/dialog"
import { Icon } from "@opencode/ui/icon"
import { IconButton } from "@opencode/ui/icon-button"
import { InlineInput } from "@opencode/ui/inline-input"
import { Menu } from "@opencode/ui/menu"
import { Tooltip } from "@opencode/ui/tooltip"
import { useQueryClient } from "@tanstack/solid-query"
import { createMemo, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import type { HomeSessionRecord } from "@/home/sessions/records"
import { useLanguage } from "@/runtime/i18n/language"
import { ServerConnection } from "@/runtime/server/registry"
import { useGlobal } from "@/runtime/server/runtime"
import { removedSessionIDs } from "@/session/session-domain"
import { sessionLabel } from "@/session/title"
import { errorMessage } from "@/shell/layout/helpers"
import { showToast } from "@/shell/notifications/toast"
import { notifySessionTabsRemoved } from "@/shell/titlebar/session-events"
import { TabPreviewPopover } from "@/shell/titlebar/tab-popover"
import { useSidebar } from "./context"
import type { SidebarSessionRef } from "./schema"
import { SidebarMenuPortal } from "./menu"

export type NavigationStatus = "attention" | "running" | "unread" | "idle"

export function sessionSidebarRef(server: ServerConnection.Key, record: HomeSessionRecord): SidebarSessionRef {
  return {
    type: "session",
    server,
    sessionID: record.session.id,
    title: record.session.title,
    directory: record.session.location.directory,
    projectID: record.session.projectID,
  }
}

export function SidebarPinIcon(props: { unpin?: boolean }) {
  return <Icon name={props.unpin ? "pin-off" : "pin"} />
}

export function SessionNavigationRow(props: {
  record: HomeSessionRecord
  server: ServerConnection.Key
  status: NavigationStatus
  active: boolean
  pinned?: boolean
  onOpen: (event: MouseEvent) => void
  onMove?: (offset: -1 | 1) => void
  canMoveUp?: boolean
  canMoveDown?: boolean
}) {
  const language = useLanguage()
  const global = useGlobal()
  const sidebar = useSidebar()
  const dialog = useDialog()
  const queryClient = useQueryClient()
  const [state, setState] = createStore({
    preview: false,
    previewBlocked: false,
    restoredFocus: false,
    menu: undefined as { x: number; y: number } | undefined,
    editor: undefined as { draft: string; saving: boolean } | undefined,
  })
  const conn = createMemo(() => global.servers.list().find((conn) => ServerConnection.key(conn) === props.server))
  const context = createMemo(() => {
    const connection = conn()
    return connection ? global.ensureServerCtx(connection) : undefined
  })
  const title = createMemo(() => sessionLabel(props.record.session, language.t))
  const ref = () => sessionSidebarRef(props.server, props.record)
  const pinned = () => props.pinned ?? sidebar.isPinned(ref())
  const statusLabel = createMemo(() => navigationStatusLabel(props.status, language))
  const updated = createMemo(() =>
    language.t("session.navigation.updated", {
      time: new Intl.DateTimeFormat(language.intl(), { dateStyle: "medium", timeStyle: "short" }).format(
        props.record.session.time.updated ?? props.record.session.time.created,
      ),
    }),
  )
  let container: HTMLDivElement | undefined
  let openButton: HTMLButtonElement | undefined
  let input: HTMLInputElement | undefined
  let outside = false
  let openingDialog = false
  let disposed = false
  onCleanup(() => {
    disposed = true
  })

  const focusRow = () => {
    // Pinning moves the row between keyed lists; never focus a different session after it moves.
    const server = props.server
    const sessionID = props.record.session.id
    requestAnimationFrame(() => {
      if (openButton?.isConnected) {
        openButton.focus()
        return
      }
      Array.from(document.querySelectorAll<HTMLButtonElement>("[data-sidebar-session-open]"))
        .find(
          (button) =>
            button.dataset.server === server &&
            button.dataset.sessionId === sessionID &&
            button.getClientRects().length > 0,
        )
        ?.focus()
    })
  }
  const openMenu = (clientX: number, clientY: number) => {
    const bounds = container?.getBoundingClientRect()
    if (!bounds || state.editor) return
    setState({ preview: false, menu: { x: clientX - bounds.left, y: clientY - bounds.top } })
  }
  const openEditor = () => {
    setState({ preview: false, editor: { draft: title(), saving: false } })
    requestAnimationFrame(() => {
      input?.focus()
      input?.select()
    })
  }
  const closeEditor = (restoreFocus = false) => {
    if (!state.editor || state.editor.saving) return
    setState({ editor: undefined, previewBlocked: restoreFocus, restoredFocus: restoreFocus })
  }
  const saveEditor = async () => {
    if (!state.editor || state.editor.saving) return
    const next = state.editor.draft.trim()
    if (!next || next === title()) {
      closeEditor(true)
      focusRow()
      return
    }
    const ctx = context()
    const connection = conn()
    if (!ctx || !connection) return
    const session = props.record.session
    const reference = ref()
    setState("editor", "saving", true)
    const saved = await ctx.sdk.api.session
      .update({ sessionID: session.id, title: next })
      .then(() => {
        ctx.data.session.remember({ ...(ctx.data.session.get(session.id) ?? session), title: next })
        sidebar.remember({ ...reference, title: next })
        ctx.data.session.invalidate(session.id)
        void ctx.data.session.sync(session.id).catch(() => undefined)
        queryClient.setQueryData<SessionInfo[]>(["home-sessions", connection], (current) =>
          current?.map((item) => (item.id === session.id ? { ...item, title: next } : item)),
        )
        return true
      })
      .catch((cause: unknown) => {
        showToast({
          variant: "error",
          title: language.t("common.requestFailed"),
          description: errorMessage(cause, language.t("common.requestFailed")),
        })
        return false
      })
    if (disposed) return
    const restore = document.activeElement === document.body || document.activeElement === input
    if (saved) setState({ editor: undefined, previewBlocked: restore, restoredFocus: restore })
    if (!saved) setState("editor", "saving", false)
    if (!restore) return
    if (saved) focusRow()
    if (!saved) requestAnimationFrame(() => input?.focus())
  }
  const showDelete = () => {
    const ctx = context()
    const connection = conn()
    if (!ctx || !connection) return
    const session = props.record.session
    const server = props.server
    const ids = [...removedSessionIDs(ctx.data.session.list(), session.id)]
    openingDialog = true
    void dialog.show(
      () => (
        <DeleteSessionDialog
          session={session}
          remove={() =>
            ctx.data.session
              .remove(session.id)
              .then(() => {
                notifySessionTabsRemoved({ server, directory: session.location.directory, sessionIDs: ids })
                return true
              })
              .catch((cause: unknown) => {
                showToast({
                  variant: "error",
                  title: language.t("session.delete.failed.title"),
                  description: errorMessage(cause, language.t("session.delete.failed.title")),
                })
                return false
              })
              .finally(() => {
                void queryClient.invalidateQueries({ queryKey: ["home-sessions", connection], exact: true })
              })
          }
        />
      ),
      () => {
        openingDialog = false
        focusRow()
      },
    )
  }

  return (
    <div
      ref={container}
      data-slot="session-navigation-row-container"
      data-active={props.active}
      data-status={props.status}
      data-pinned={pinned()}
      class="relative flex min-w-0 items-center"
      onContextMenu={(event) => {
        if (state.editor) return
        event.preventDefault()
        event.stopPropagation()
        openMenu(event.clientX, event.clientY)
      }}
    >
      <Show
        when={!state.editor}
        fallback={
          <div data-slot="session-navigation-editor">
            <InlineInput
              ref={input}
              data-slot="session-navigation-rename"
              aria-label={language.t("common.rename")}
              dir="auto"
              value={state.editor?.draft ?? ""}
              disabled={state.editor?.saving}
              onInput={(event) => setState("editor", "draft", event.currentTarget.value)}
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                event.stopPropagation()
                if (event.isComposing || event.keyCode === 229) return
                if (event.key === "Enter") {
                  event.preventDefault()
                  void saveEditor()
                }
                if (event.key !== "Escape") return
                event.preventDefault()
                closeEditor(true)
                focusRow()
              }}
              onBlur={() => closeEditor()}
            />
          </div>
        }
      >
        <TabPreviewPopover
          orientation="vertical"
          open={state.preview && !state.menu && !state.previewBlocked}
          onOpenChange={(open) => setState("preview", open && !state.menu && !state.editor && !state.previewBlocked)}
          data={{
            projectName: props.record.projectName,
            title: title(),
            path: props.record.session.location.directory,
            updated: updated(),
            status: statusLabel(),
          }}
          trigger={
            <button
              ref={openButton}
              type="button"
              data-component="session-navigation-row"
              data-sidebar-session-open=""
              data-server={props.server}
              data-session-id={props.record.session.id}
              data-pinned={pinned()}
              data-active={props.active}
              data-status={props.status}
              data-restored-focus={state.restoredFocus || undefined}
              aria-current={props.active ? "page" : undefined}
              aria-haspopup="menu"
              aria-expanded={!!state.menu}
              aria-description={[props.record.projectName, updated(), statusLabel()].filter(Boolean).join(", ")}
              onClick={props.onOpen}
              onBlur={() => setState({ restoredFocus: false, previewBlocked: false })}
              onPointerMove={(event) => {
                // Replacing the input can emit pointer-enter events without the mouse moving.
                if (event.movementX || event.movementY) setState("previewBlocked", false)
              }}
              onPointerDown={() => setState("restoredFocus", false)}
              onMouseDown={(event) => {
                if (event.button === 1) event.preventDefault()
              }}
              onAuxClick={(event) => {
                if (event.button === 1) props.onOpen(event)
              }}
              onKeyDown={(event) => {
                // Restoring focus after editing keeps keyboard navigation in place without reopening the preview.
                setState({ restoredFocus: false, previewBlocked: false })
                if (event.key !== "ContextMenu" && (event.key !== "F10" || !event.shiftKey)) return
                event.preventDefault()
                event.stopPropagation()
                const bounds = event.currentTarget.getBoundingClientRect()
                openMenu(bounds.left + 12, bounds.bottom)
              }}
            >
              <span data-slot="session-navigation-title" dir="auto">
                {title()}
              </span>
              <Show when={props.status !== "idle"}>
                <span data-slot="session-navigation-status" aria-label={statusLabel()}>
                  <span data-slot="session-navigation-status-dot" aria-hidden="true" />
                </span>
              </Show>
            </button>
          }
        />
      </Show>
      <Show when={!state.editor}>
        <Tooltip value={language.t("session.navigation.options", { title: title() })} placement="bottom">
          <IconButton
            data-action="session-navigation-session-menu"
            variant="ghost-muted"
            size="small"
            icon={<Icon name="outline-dots" size="small" />}
            aria-label={language.t("session.navigation.options", { title: title() })}
            aria-haspopup="menu"
            aria-expanded={!!state.menu}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              const bounds = event.currentTarget.getBoundingClientRect()
              openMenu(bounds.left, bounds.bottom)
            }}
          />
        </Tooltip>
      </Show>
      <Menu
        modal={false}
        placement="bottom-start"
        gutter={2}
        open={!!state.menu}
        onOpenChange={(open) => {
          if (!open) setState("menu", undefined)
        }}
      >
        <Menu.Trigger
          as="span"
          aria-hidden="true"
          tabIndex={-1}
          class="pointer-events-none absolute size-px"
          style={{ left: `${state.menu?.x ?? 0}px`, top: `${state.menu?.y ?? 0}px` }}
        />
        <SidebarMenuPortal>
          <Menu.Content
            onInteractOutside={() => {
              outside = true
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              const restore = !outside && !state.editor && !openingDialog
              outside = false
              if (restore) focusRow()
            }}
          >
            <Menu.Item onSelect={() => (pinned() ? sidebar.unpin(ref()) : sidebar.pin(ref()))}>
              <SidebarPinIcon unpin={pinned()} />
              {language.t(pinned() ? "session.navigation.unpin" : "session.navigation.pin")}
            </Menu.Item>
            <Menu.Item disabled={!context()} onSelect={openEditor}>
              <Icon name="edit" size="small" />
              {language.t("common.rename")}
            </Menu.Item>
            <Show when={props.onMove}>
              <Menu.Separator />
              <Menu.Item disabled={!props.canMoveUp} onSelect={() => props.onMove?.(-1)}>
                <Icon name="arrow-up" size="small" />
                {language.t("session.navigation.moveUp")}
              </Menu.Item>
              <Menu.Item disabled={!props.canMoveDown} onSelect={() => props.onMove?.(1)}>
                <Icon name="arrow-up" size="small" class="rotate-180" />
                {language.t("session.navigation.moveDown")}
              </Menu.Item>
            </Show>
            <Menu.Separator />
            <Menu.Item disabled={!context()} onSelect={showDelete}>
              <Icon name="trash" size="small" />
              {language.t("common.delete")}
            </Menu.Item>
          </Menu.Content>
        </SidebarMenuPortal>
      </Menu>
    </div>
  )
}

function DeleteSessionDialog(props: { session: SessionInfo; remove: () => Promise<boolean> }) {
  const language = useLanguage()
  const dialog = useDialog()
  const [state, setState] = createStore({ deleting: false })
  const confirm = async () => {
    if (state.deleting) return
    setState("deleting", true)
    const removed = await props.remove()
    setState("deleting", false)
    if (removed) dialog.close()
  }
  return (
    <Dialog
      fit
      containerClass="sair-project-dialog-container sair-confirm-dialog-container"
      class="sair-project-dialog"
    >
      <DialogHeader hideClose>
        <DialogTitleGroup
          title={language.t("session.delete.title")}
          description={language.t("session.delete.confirm", { name: sessionLabel(props.session, language.t) })}
        />
      </DialogHeader>
      <DialogFooter>
        <Button autofocus variant="neutral" disabled={state.deleting} onClick={() => dialog.close()}>
          {language.t("common.cancel")}
        </Button>
        <Button variant="danger" disabled={state.deleting} onClick={() => void confirm()}>
          {language.t("session.delete.button")}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export function navigationStatusLabel(status: NavigationStatus, language: ReturnType<typeof useLanguage>) {
  if (status === "attention") return language.t("session.navigation.attention")
  if (status === "running") return language.t("session.navigation.running")
  if (status === "unread") return language.t("session.navigation.unread")
  return ""
}
